import { useEffect, useState } from 'react';

import { Kicker, Spinner } from '../../components/primitives.tsx';
import { TranscriptSummary, useTranscriptSummary } from '../../components/transcript-summary.tsx';
import { api } from '../../live/api.ts';
import { extractReadableLines, missingTranscriptNote, type TranscriptExpiry } from '../../lib/transcript.ts';

const TRANSCRIPT_WINDOW = 192 * 1024;

/**
 * The readable tail of one attempt's transcript. Used by the Activity tab when a run is
 * addressed by URL and by the Review tab when a person wants to see how a reviewer reached
 * its verdict. Fetches on mount, so mount it lazily.
 */
export function RunTranscript({ runId, maxHeight = 360 }: { runId: number; maxHeight?: number }) {
  const [payload, setPayload] = useState<({
    size: number;
    text: string;
    missing: boolean;
  } & TranscriptExpiry) | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const summariser = useTranscriptSummary(runId);

  async function load(): Promise<void> {
    setBusy(true);
    try {
      setPayload(await api.transcript(runId, { bytes: TRANSCRIPT_WINDOW }));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    setPayload(null);
    summariser.reset();
    void load();
    // `load` is intentionally local to this small, mount-only pane.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, summariser.reset]);

  const readable = payload ? extractReadableLines(payload.text) : [];
  return (
    <section className="runs-transcript" aria-label={`Transcript for attempt ${runId}`}>
      <header className="panel-head">
        <Kicker>Transcript</Kicker>
        <span className="mono dim" style={{ fontSize: 'var(--fs-micro)' }}>
          attempt {runId}
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ marginLeft: 'auto' }}
          disabled={busy}
          onClick={() => void load()}
        >
          {busy ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={summariser.busy || payload === null || payload.missing}
          title="Summarise the tail of this transcript with a small model"
          onClick={() => void summariser.summarise()}
        >
          {summariser.label}
        </button>
      </header>
      <TranscriptSummary
        summary={summariser.summary}
        busy={summariser.busy}
        error={summariser.error}
        transcriptSize={payload && !payload.missing ? payload.size : null}
      />
      {error ? (
        <p style={{ color: 'var(--danger-text)' }}>{error}</p>
      ) : payload?.missing ? (
        <p className="sheet-note">{missingTranscriptNote(payload)}</p>
      ) : payload === null ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s6)' }}>
          <Spinner size={18} />
        </div>
      ) : readable.length === 0 ? (
        <p className="sheet-note">Nothing readable in the tail of this transcript.</p>
      ) : (
        <div className="transcript scroll-y" style={{ maxHeight }}>
          {readable.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      )}
    </section>
  );
}
